import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Alert Management Service
 * Handles alert actions: acknowledge, snooze, route, escalate
 */

export const alertManagementService = {
  /**
   * Snooze an alert until a specific time
   * @param {string} eventId - Platform event ID
   * @param {Date} snoozeUntil - When to un-snooze the alert
   * @param {string} notes - Optional notes
   * @returns {Promise<{data, error}>}
   */
  async snoozeAlert(eventId, snoozeUntil, notes = '') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('alert_actions')
        ?.insert([
          {
            event_id: eventId,
            action_type: 'snoozed',
            performed_by: user?.id,
            snoozed_until: snoozeUntil?.toISOString(),
            notes
          }
        ])
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to snooze alert', error, { requestId, eventId });
        return { data: null, error };
      }

      logger?.audit('ALERT_SNOOZED', user?.id, { requestId, eventId, snoozeUntil });
      return { data, error: null };
    } catch (error) {
      logger?.error('Snooze alert error', error, { requestId, eventId });
      return { data: null, error: { message: 'Failed to snooze alert' } };
    }
  },

  /**
   * Route an alert to another officer
   * @param {string} eventId - Platform event ID
   * @param {string} routeToUserId - User ID to route to
   * @param {string} notes - Optional notes
   * @returns {Promise<{data, error}>}
   */
  async routeAlert(eventId, routeToUserId, notes = '') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('alert_actions')
        ?.insert([
          {
            event_id: eventId,
            action_type: 'routed',
            performed_by: user?.id,
            routed_to: routeToUserId,
            notes
          }
        ])
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to route alert', error, { requestId, eventId });
        return { data: null, error };
      }

      logger?.audit('ALERT_ROUTED', user?.id, { requestId, eventId, routeToUserId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Route alert error', error, { requestId, eventId });
      return { data: null, error: { message: 'Failed to route alert' } };
    }
  },

  /**
   * Escalate an alert
   * @param {string} eventId - Platform event ID
   * @param {string} notes - Escalation notes
   * @returns {Promise<{data, error}>}
   */
  async escalateAlert(eventId, notes = '') {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        return { data: null, error: { message: 'Not authenticated' } };
      }

      const { data, error } = await supabase
        ?.from('alert_actions')
        ?.insert([
          {
            event_id: eventId,
            action_type: 'escalated',
            performed_by: user?.id,
            notes
          }
        ])
        ?.select()
        ?.single();

      if (error) {
        logger?.error('Failed to escalate alert', error, { requestId, eventId });
        return { data: null, error };
      }

      logger?.audit('ALERT_ESCALATED', user?.id, { requestId, eventId });
      return { data, error: null };
    } catch (error) {
      logger?.error('Escalate alert error', error, { requestId, eventId });
      return { data: null, error: { message: 'Failed to escalate alert' } };
    }
  },

  /**
   * Get alert actions for an event
   * @param {string} eventId - Platform event ID
   * @returns {Promise<{data, error}>}
   */
  async getAlertActions(eventId) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data, error } = await supabase
        ?.from('alert_actions')
        ?.select(`
          *,
          performed_by_profile:user_profiles!alert_actions_performed_by_fkey(id, email, full_name),
          routed_to_profile:user_profiles!alert_actions_routed_to_fkey(id, email, full_name)
        `)
        ?.eq('event_id', eventId)
        ?.order('created_at', { ascending: false });

      if (error) {
        logger?.error('Failed to fetch alert actions', error, { requestId, eventId });
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Get alert actions error', error, { requestId, eventId });
      return { data: null, error: { message: 'Failed to fetch alert actions' } };
    }
  },

  /**
   * Get escalation rules
   * @param {Object} options - Filter options
   * @returns {Promise<{data, error}>}
   */
  async getEscalationRules(options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      let query = supabase
        ?.from('escalation_rules')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('priority', { ascending: false });

      if (options?.eventType) {
        query = query?.eq('event_type', options?.eventType);
      }

      if (options?.severity) {
        query = query?.eq('severity', options?.severity);
      }

      const { data, error } = await query;

      if (error) {
        logger?.error('Failed to fetch escalation rules', error, { requestId });
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Get escalation rules error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch escalation rules' } };
    }
  },

  /**
   * Get response template for event
   * @param {string} eventType - Event type
   * @param {string} severity - Event severity
   * @returns {Promise<{data, error}>}
   */
  async getResponseTemplate(eventType, severity) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data, error } = await supabase
        ?.from('response_templates')
        ?.select('*')
        ?.eq('event_type', eventType)
        ?.eq('severity', severity)
        ?.eq('is_active', true)
        ?.limit(1)
        ?.single();

      if (error && error?.code !== 'PGRST116') {
        logger?.error('Failed to fetch response template', error, { requestId, eventType, severity });
        return { data: null, error };
      }

      return { data: data || null, error: null };
    } catch (error) {
      logger?.error('Get response template error', error, { requestId, eventType, severity });
      return { data: null, error: { message: 'Failed to fetch response template' } };
    }
  },

  /**
   * Get all response templates
   * @returns {Promise<{data, error}>}
   */
  async getAllResponseTemplates() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const { data, error } = await supabase
        ?.from('response_templates')
        ?.select('*')
        ?.eq('is_active', true)
        ?.order('severity', { ascending: false });

      if (error) {
        logger?.error('Failed to fetch response templates', error, { requestId });
        return { data: null, error };
      }

      return { data, error: null };
    } catch (error) {
      logger?.error('Get all response templates error', error, { requestId });
      return { data: null, error: { message: 'Failed to fetch response templates' } };
    }
  },

  /**
   * Check if alert should be escalated based on rules
   * @param {Object} event - Platform event
   * @returns {Promise<{shouldEscalate: boolean, rule: Object}>}
   */
  async checkEscalation(event) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Get matching escalation rules
      const { data: rules } = await this.getEscalationRules({
        eventType: event?.eventType,
        severity: event?.eventSeverity
      });

      if (!rules || rules?.length === 0) {
        return { shouldEscalate: false, rule: null };
      }

      // Check if event is old enough to escalate
      const eventTime = new Date(event?.createdAt);
      const now = new Date();
      const minutesSinceEvent = (now - eventTime) / (1000 * 60);

      for (const rule of rules) {
        if (minutesSinceEvent >= rule?.escalate_after_minutes && !event?.acknowledged) {
          return { shouldEscalate: true, rule };
        }
      }

      return { shouldEscalate: false, rule: null };
    } catch (error) {
      logger?.error('Check escalation error', error, { requestId });
      return { shouldEscalate: false, rule: null };
    }
  }
};

export default alertManagementService;